import { Container, Section, Select } from '@resolute/ui';
import {
  calculateRentalYield,
  convertFromInr,
  FX_AS_OF,
  formatCurrency,
  formatINRCompact,
  projectInvestmentReturn,
  type SupportedCurrency,
} from '@resolute/utils';
import { useMemo, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';


import { CalcLayout, ResultStat, SliderField, ToolDisclaimer } from './components/calc-ui';

import { Seo } from '@/lib/seo';


const CURRENCY_OPTIONS: { value: SupportedCurrency; label: string }[] = [
  { value: 'INR', label: '₹ INR' },
  { value: 'USD', label: '$ USD' },
  { value: 'AED', label: 'د.إ AED' },
  { value: 'GBP', label: '£ GBP' },
  { value: 'EUR', label: '€ EUR' },
  { value: 'SGD', label: '$ SGD' },
  { value: 'CAD', label: '$ CAD' },
  { value: 'AUD', label: '$ AUD' },
];

export default function RentalYieldCalculatorPage() {
  const [currency, setCurrency] = useState<SupportedCurrency>('INR');
  const [price, setPrice] = useState(20_000_000);
  const [monthlyRent, setMonthlyRent] = useState(110_000);
  const [annualCost, setAnnualCost] = useState(150_000);
  const [escalation, setEscalation] = useState(5);
  const [appreciation, setAppreciation] = useState(7);
  const [holdingYears, setHoldingYears] = useState(5);

  const yields = useMemo(
    () => calculateRentalYield({ price, monthlyRent, annualCost }),
    [price, monthlyRent, annualCost],
  );

  const projection = useMemo(
    () =>
      projectInvestmentReturn({
        price,
        monthlyRent,
        annualCost,
        rentEscalationPct: escalation,
        appreciationPct: appreciation,
        holdingYears,
      }),
    [price, monthlyRent, annualCost, escalation, appreciation, holdingYears],
  );

  // Format an INR value in the chosen display currency (compact).
  const money = (inr: number) =>
    currency === 'INR'
      ? formatINRCompact(inr)
      : formatCurrency(convertFromInr(inr, currency), currency, {
          notation: 'compact',
          maximumFractionDigits: 1,
        });

  const chartData = projection.years.map((y) => ({
    year: `Y${y.year}`,
    value: currency === 'INR' ? y.cumulativeReturn : convertFromInr(y.cumulativeReturn, currency),
  }));

  return (
    <Section>
      <Seo
        title="Rental Yield & ROI Calculator"
        description="Calculate gross and net rental yield, cap rate and multi-year ROI for pre-leased and investment property. Built for investors and NRIs."
        path="/calculators/rental-yield"
      />
      <Container className="max-w-5xl">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Rental Yield &amp; ROI Calculator
            </h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Model yield and total return over your holding period — combining rental income,
              escalation and capital appreciation.
            </p>
          </div>
          <div className="w-40">
            <Select
              label="Display currency"
              options={CURRENCY_OPTIONS}
              value={currency}
              onChange={(e) => setCurrency(e.target.value as SupportedCurrency)}
            />
          </div>
        </header>

        <CalcLayout
          controls={
            <>
              <SliderField
                label="Purchase price"
                value={price}
                onChange={setPrice}
                min={2_500_000}
                max={200_000_000}
                step={500_000}
                display={money}
              />
              <SliderField
                label="Monthly rent"
                value={monthlyRent}
                onChange={setMonthlyRent}
                min={5_000}
                max={2_000_000}
                step={5_000}
                display={money}
              />
              <SliderField
                label="Annual operating cost"
                value={annualCost}
                onChange={setAnnualCost}
                min={0}
                max={2_000_000}
                step={10_000}
                display={money}
                hint="Property tax, maintenance, vacancy, management."
              />
              <SliderField
                label="Annual rent escalation"
                value={escalation}
                onChange={setEscalation}
                min={0}
                max={15}
                step={0.5}
                display={(v) => `${v}%`}
              />
              <SliderField
                label="Annual price appreciation"
                value={appreciation}
                onChange={setAppreciation}
                min={0}
                max={20}
                step={0.5}
                display={(v) => `${v}%`}
              />
              <SliderField
                label="Holding period"
                value={holdingYears}
                onChange={setHoldingYears}
                min={1}
                max={25}
                step={1}
                display={(v) => `${v} ${v === 1 ? 'year' : 'years'}`}
              />
            </>
          }
          results={
            <>
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <ResultStat label="Gross yield" value={`${yields.grossYieldPct.toFixed(2)}%`} />
                <ResultStat label="Net yield" value={`${yields.netYieldPct.toFixed(2)}%`} />
                <ResultStat label="Cap rate" value={`${yields.capRatePct.toFixed(2)}%`} />
              </dl>

              <div>
                <ResultStat
                  label={`Total return over ${holdingYears} ${holdingYears === 1 ? 'year' : 'years'}`}
                  value={money(projection.totalReturn)}
                  emphasis
                  tone="success"
                />
                <p className="mt-1 text-sm text-muted-foreground">
                  {projection.totalReturnPct.toFixed(0)}% total · {projection.annualizedReturnPct.toFixed(1)}% annualised
                </p>
              </div>

              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <defs>
                      <linearGradient id="roiFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--success))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--success))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="year"
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      width={56}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) =>
                        currency === 'INR'
                          ? formatINRCompact(v)
                          : formatCurrency(v, currency, { notation: 'compact', maximumFractionDigits: 1 })
                      }
                    />
                    <Tooltip
                      formatter={(v: number) => [
                        currency === 'INR'
                          ? formatINRCompact(v)
                          : formatCurrency(v, currency, { maximumFractionDigits: 0 }),
                        'Cumulative return',
                      ]}
                      contentStyle={{
                        background: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                    <Area type="monotone" dataKey="value" stroke="hsl(var(--success))" strokeWidth={2} fill="url(#roiFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <dl className="grid grid-cols-2 gap-4">
                <ResultStat label="Projected exit value" value={money(projection.exitValue)} tone="foreground" />
                <ResultStat label="Rental income (total)" value={money(projection.totalRentalIncome)} tone="foreground" />
              </dl>
              <ToolDisclaimer>
                Indicative only — assumptions are yours, not a forecast or advice. Currency conversions
                are approximate (as of {FX_AS_OF}).
              </ToolDisclaimer>
            </>
          }
        />
      </Container>
    </Section>
  );
}
